// src/user/EarningsSummary.js
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Typography, Box, Paper, Container, Button, Chip, Divider, Grid, Tooltip, Alert, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, AccountBalanceWallet, People, VideoLibrary, Payment, Schedule, SettingsApplications } from '@mui/icons-material';
import api from '../utils/axiosInstance';
import { alpha } from '@mui/material/styles';
import { API } from '../config/apiConfig';
import { portal } from '../styles/dashboardBrand';
import WelcomeHeader from '../components/WelcomeHeader';
import DashboardPageHeader from '../components/DashboardPageHeader';
import KYCStatusIndicator from '../components/KYCStatusIndicator';
import { normalizeKYCStatus } from '../utils/kycUtils';
import { dreameyDiamondsUserSum } from '../utils/dreameyDiamonds';
import {
  readHomeCache,
  readUserSessionProfileCache,
  mergeDashboardRawFields,
  readPersistentHomeRankProgressCache,
  mergePersistentHomeRankProgressCache,
  clearUserDashboardHomeForceNetwork,
} from '../utils/userDashboardHomeCache';
import {
  readUserRedemptionStatusCache,
  mergeUserRedemptionStatusCache,
} from '../utils/userRedemptionStatusCache';
import { prefetchUserRedemptionStatus } from '../utils/userPostLoginPrefetch';

/** Glass-style hero for My Summary title row (dark / light aware). */
const summaryHeaderSurfaceSx = (t) => ({
  p: { xs: 2, sm: 2.5 },
  borderRadius: 3,
  position: 'relative',
  overflow: 'hidden',
  border: `1px solid ${alpha(portal.cyan, t.palette.mode === 'dark' ? 0.22 : 0.2)}`,
  background:
    t.palette.mode === 'dark'
      ? `linear-gradient(145deg, ${alpha('#1a1f30', 0.85)} 0%, ${alpha('#0f111a', 0.92)} 100%)`
      : `linear-gradient(145deg, ${alpha('#fff', 0.95)} 0%, ${alpha('#f0f7ff', 0.88)} 100%)`,
  boxShadow:
    t.palette.mode === 'dark'
      ? `0 12px 40px ${alpha('#000', 0.35)}, inset 0 1px 0 ${alpha('#fff', 0.06)}`
      : `0 8px 32px ${alpha(portal.cyan, 0.12)}`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: portal.gradientBar,
    opacity: 0.9,
  },
});

function EarningsSummary() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [nextPayment, setNextPayment] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [availableBalance, setAvailableBalance] = useState(null);
  const [hasCNIC, setHasCNIC] = useState(null);
  const [isChild, setIsChild] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(true); // Default to true (no notification)
  // Persistent "redemption readiness" flags (filled by post-login prefetch
  // and by the save flows in Profile / Redemptions). When all three flags
  // read true we can hide the redemption notice without any network calls.
  const [redemptionStatusFlags, setRedemptionStatusFlags] = useState(() => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      if (!token) return null;
      const userId = JSON.parse(atob(token.split('.')[1])).userId;
      return readUserRedemptionStatusCache(userId);
    } catch {
      return null;
    }
  });
  const [streakForSummary, setStreakForSummary] = useState({ loading: true, streakDays: 0 });
  const [dreameyDiamondsTotal, setDreameyDiamondsTotal] = useState(0);

  const sumEngagementPointsFromLedgerPayload = (payload) => {
    const items = Array.isArray(payload) ? payload : (payload?.ledger ?? payload?.data ?? []);
    return Array.isArray(items)
      ? items.reduce((sum, tx) => sum + (Number(tx?.TokensGenerated ?? tx?.Tokens ?? 0) || 0), 0)
      : 0;
  };

  useLayoutEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (!token) return;
    let userId;
    try {
      userId = JSON.parse(atob(token.split('.')[1])).userId;
    } catch {
      return;
    }
    const homeCache = readHomeCache(userId);
    const raw = homeCache?.raw;
    const selfInfo = homeCache?.selfInfo;
    const persistentConnections = readPersistentHomeRankProgressCache(userId)?.communityConnections;
    if (!raw && !persistentConnections) return;

    if (Number.isFinite(Number(selfInfo?.totalDreameyDiamonds))) {
      setDreameyDiamondsTotal(Number(selfInfo.totalDreameyDiamonds));
    } else if (raw?.coins?.success) {
      setDreameyDiamondsTotal(dreameyDiamondsUserSum(raw.coins));
    }
    const engagementPoints = Number.isFinite(Number(selfInfo?.engagementPoints))
      ? Number(selfInfo.engagementPoints)
      : raw?.ledger
        ? sumEngagementPointsFromLedgerPayload(raw.ledger)
        : null;
    if (engagementPoints !== null) {
      setSummary((prev) => ({
        ...(prev ?? {}),
        name: selfInfo?.name ?? prev?.name,
        currentMonthTokens: engagementPoints,
        CurrentMonthTokens: engagementPoints,
      }));
    }
    const cachedEarnings = raw?.earnings?.earnings ?? raw?.earnings?.data ?? raw?.earnings ?? null;
    const cachedActiveConnections = Number(
      cachedEarnings?.ActiveConnections ??
        cachedEarnings?.activeConnections ??
        persistentConnections?.ActiveConnections ??
        persistentConnections?.activeConnections
    );
    if (Number.isFinite(cachedActiveConnections)) {
      setSummary((prev) => ({
        ...(prev ?? {}),
        ActiveConnections: cachedActiveConnections,
        activeConnections: cachedActiveConnections,
      }));
      setLoading(false);
    }
    const profileData = raw?.profile?.user ?? raw?.profile ?? {};
    const countryOfResidenceId =
      profileData.CountryOfResidenceId ??
      profileData.CountryOfResidenceID ??
      profileData.countryOfResidenceId ??
      null;
    setIsProfileComplete(countryOfResidenceId !== null && countryOfResidenceId !== undefined);
    const countryCode = profileData.CountryCode ?? profileData.countryCode ?? null;
    setUserProfile({
      id: userId,
      country: countryCode,
      countryCode: countryCode,
      region: profileData.Region ?? null,
    });
    setKycStatus({ verified: false, status: 'not_started' });
    if (Number.isFinite(Number(selfInfo?.currentStreak))) {
      setStreakForSummary({ loading: false, streakDays: Math.max(0, Number(selfInfo.currentStreak) || 0) });
    } else if (raw?.streak) {
      const data = raw.streak;
      const r = data?.data && typeof data.data === 'object' ? data.data : data;
      const streakDays = Math.max(0, Number(r?.currentStreak ?? r?.CurrentStreak ?? 0) || 0);
      setStreakForSummary({ loading: false, streakDays });
    }
    if (raw && 'walletBalance' in raw) {
      const balanceValue = Number(raw.walletBalance);
      setAvailableBalance(Number.isNaN(balanceValue) ? null : balanceValue);
    }
    const ps = raw?.paymentSummary;
    if (ps && typeof ps === 'object') {
      if (ps.paymentSettings != null) setPaymentSettings(ps.paymentSettings);
      if (ps.nextPayment != null) setNextPayment(ps.nextPayment);
      if (typeof ps.hasCNIC === 'boolean') setHasCNIC(ps.hasCNIC);
    }
    if (typeof raw?.isChild === 'boolean') {
      setIsChild(raw.isChild);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (!token) return;
    let userId;
    try {
      userId = JSON.parse(atob(token.split('.')[1])).userId;
    } catch {
      return;
    }
    const homeCache = readHomeCache(userId);
    const raw = homeCache?.raw;
    const selfInfo = homeCache?.selfInfo;
    if (Number.isFinite(Number(selfInfo?.totalDreameyDiamonds))) {
      setDreameyDiamondsTotal(Number(selfInfo.totalDreameyDiamonds));
      return;
    }
    if (raw?.coins?.success) {
      setDreameyDiamondsTotal(dreameyDiamondsUserSum(raw.coins));
      return;
    }
    setDreameyDiamondsTotal(0);
  }, []);

  // Load user profile and KYC status
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const decoded = JSON.parse(atob(token.split('.')[1]));
          const userId = decoded.userId;

          const cachedProfile = readUserSessionProfileCache(userId);
          if (cachedProfile?.profile) {
            const profileData = cachedProfile.profile?.user ?? cachedProfile.profile ?? {};
            const countryOfResidenceId =
              profileData.CountryOfResidenceId ??
              profileData.CountryOfResidenceID ??
              profileData.countryOfResidenceId ??
              null;
            setIsProfileComplete(countryOfResidenceId !== null && countryOfResidenceId !== undefined);
            const countryCode = profileData.CountryCode ?? profileData.countryCode ?? decoded.countryCode ?? decoded.country ?? null;
            setUserProfile({
              id: userId,
              country: countryCode,
              countryCode: countryCode,
              region: profileData.Region ?? decoded.region ?? null,
            });
            setKycStatus({ verified: false, status: 'not_started' });
            return;
          }

          const raw = readHomeCache(userId)?.raw;
          const rawProfile = raw?.profile?.user ?? raw?.profile ?? null;
          if (rawProfile) {
            const countryOfResidenceId =
              rawProfile.CountryOfResidenceId ??
              rawProfile.CountryOfResidenceID ??
              rawProfile.countryOfResidenceId ??
              null;
            setIsProfileComplete(countryOfResidenceId !== null && countryOfResidenceId !== undefined);
            const countryCode = rawProfile.CountryCode ?? rawProfile.countryCode ?? decoded.countryCode ?? decoded.country ?? null;
            setUserProfile({
              id: userId,
              country: countryCode,
              countryCode: countryCode,
              region: rawProfile.Region ?? decoded.region ?? null,
            });
            setKycStatus({ verified: false, status: 'not_started' });
            return;
          }

          setUserProfile({
            id: userId,
            country: decoded.countryCode || decoded.country || null,
            region: decoded.region || null,
          });
          setIsProfileComplete(true);
          
          // Set default KYC status without API call
          setKycStatus({
            verified: false,
            status: 'not_started'
          });
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };

    loadUserProfile();
  }, []);

  useEffect(() => {
    const fetchEarnings = async () => {
      try {
        // Get userId from token
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No token found');
          setLoading(false);
          return;
        }

        const decoded = JSON.parse(atob(token.split('.')[1]));
        const userId = decoded.userId;
        const persistentConnections = readPersistentHomeRankProgressCache(userId)?.communityConnections;
        const previousActiveConnections = Number(
          persistentConnections?.ActiveConnections ??
            persistentConnections?.activeConnections ??
            readHomeCache(userId)?.raw?.earnings?.earnings?.ActiveConnections ??
            readHomeCache(userId)?.raw?.earnings?.earnings?.activeConnections
        );
        if (Number.isFinite(previousActiveConnections)) {
          setSummary((prev) => ({
            ...(prev ?? {}),
            ActiveConnections: previousActiveConnections,
            activeConnections: previousActiveConnections,
          }));
        }

        const response = await api.get(`/earnings/summary/${userId}`);

        if (response.data.success) {
          const earnings = response.data.earnings ?? {};
          const responseActiveConnections = Number(earnings.ActiveConnections ?? earnings.activeConnections);
          const activeConnections = Number.isFinite(responseActiveConnections)
            ? Math.max(responseActiveConnections, Number.isFinite(previousActiveConnections) ? previousActiveConnections : 0)
            : Number.isFinite(previousActiveConnections)
              ? previousActiveConnections
              : 0;
          setSummary((prev) => ({
            ...(prev ?? {}),
            ActiveConnections: activeConnections,
            activeConnections: activeConnections,
          }));
          mergeDashboardRawFields(userId, {
            earnings: {
              success: true,
              earnings: {
                ActiveConnections: activeConnections,
                activeConnections: activeConnections,
              },
            },
          });
          mergePersistentHomeRankProgressCache(userId, {
            communityConnections: {
              ActiveConnections: activeConnections,
              activeConnections: activeConnections,
            },
          });
          clearUserDashboardHomeForceNetwork();
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching earnings summary:', err);
        setLoading(false);
      }
    };

    fetchEarnings();
  }, []);

  useEffect(() => {
    const fetchAvailableBalance = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const userId = JSON.parse(atob(token.split('.')[1])).userId;
        const raw = readHomeCache(userId)?.raw;
        if (raw && 'walletBalance' in raw) {
          const balanceValue = Number(raw.walletBalance);
          setAvailableBalance(Number.isNaN(balanceValue) ? null : balanceValue);
        }
        const response = await api.get(API.walletBalance);
        const balanceValue = Number(response?.data?.balance);
        const next = Number.isNaN(balanceValue) ? null : balanceValue;
        setAvailableBalance(next);
        mergeDashboardRawFields(userId, { walletBalance: next });
      } catch (error) {
        console.error('Error fetching wallet balance:', error);
        setAvailableBalance(null);
      }
    };

    fetchAvailableBalance();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    let userId;
    try {
      userId = JSON.parse(atob(token.split('.')[1])).userId;
    } catch {
      return;
    }

    // Fast-path: persistent cache already says the user has a saved payment
    // account → skip the API call entirely and clear the "incomplete" flag.
    const cached = readUserRedemptionStatusCache(userId);
    if (cached?.hasPaymentAccount) {
      setHasCNIC(true);
      setRedemptionStatusFlags(cached);
      return;
    }

    const fetchRedemptionStatus = async () => {
      try {
        const response = await api.get(API.savedPaymentAccounts);
        const accounts = Array.isArray(response?.data?.accounts) ? response.data.accounts : [];
        const jazzCashAccount = accounts.find((account) => {
          const method = account?.PaymentMethodName || account?.Provider;
          return method === 'JazzCash';
        });
        // The mere existence of the JazzCash account proves a CNIC was saved
        // at create time. The backend frequently omits CNIC on subsequent
        // fetches (privacy), so don't gate completion on the response field.
        const hasSavedJazzCash = Boolean(jazzCashAccount);
        setHasCNIC(hasSavedJazzCash);
        mergeDashboardRawFields(userId, {
          paymentSummary: {
            hasCNIC: hasSavedJazzCash,
          },
        });
        if (hasSavedJazzCash) {
          mergeUserRedemptionStatusCache(userId, { hasPaymentAccount: true });
          setRedemptionStatusFlags(readUserRedemptionStatusCache(userId));
        }
      } catch (error) {
        console.error('Error fetching redemption status:', error);
      }
    };

    fetchRedemptionStatus();

    // Also nudge the post-login prefetcher in case other flags (terms /
    // profile complete) still need warming.
    prefetchUserRedemptionStatus()
      .then((status) => {
        if (status) setRedemptionStatusFlags(status);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setStreakForSummary({ loading: false, streakDays: 0 });
      return;
    }
    let userId;
    try {
      userId = JSON.parse(atob(token.split('.')[1])).userId;
    } catch {
      setStreakForSummary({ loading: false, streakDays: 0 });
      return;
    }
    const homeCache = readHomeCache(userId);
    const raw = homeCache?.raw;
    const selfInfo = homeCache?.selfInfo;
    if (Number.isFinite(Number(selfInfo?.currentStreak))) {
      setStreakForSummary({ loading: false, streakDays: Math.max(0, Number(selfInfo.currentStreak) || 0) });
      return;
    }
    if (raw?.streak) {
      const data = raw.streak;
      const r = data?.data && typeof data.data === 'object' ? data.data : data;
      const streakDays = Math.max(0, Number(r?.currentStreak ?? r?.CurrentStreak ?? 0) || 0);
      setStreakForSummary({ loading: false, streakDays });
      return;
    }
    setStreakForSummary({ loading: false, streakDays: 0 });
  }, []);

  const streakDiamondMetrics = {
    streakDays: streakForSummary.streakDays,
    diamondCount: dreameyDiamondsTotal,
    loading: streakForSummary.loading,
  };

  // Prepare middle content for WelcomeHeader (alert for Pakistan users)
  // Don't show this notice to child members
  const isPakistan = userProfile?.country?.toUpperCase() === 'PK' || userProfile?.countryCode?.toUpperCase() === 'PK';

  // Use the persistent redemption-status cache as the source of truth when
  // it has anything to say. This is the cache populated post-login (and
  // updated whenever the user saves their profile / payment account /
  // accepts terms). If the cache asserts a milestone is complete we trust
  // it and skip the corresponding "incomplete" notice even before any of
  // this page's local effects have finished running.
  const cachedProfileComplete = redemptionStatusFlags?.profileComplete === true;
  const cachedHasAccount = redemptionStatusFlags?.hasPaymentAccount === true;

  // Determine what's incomplete
  const profileIncomplete = isPakistan && !cachedProfileComplete && !isProfileComplete && !isChild;
  const redemptionIncomplete = isPakistan && !cachedHasAccount && hasCNIC === false && !isChild;
  const showAlert = profileIncomplete || redemptionIncomplete;
  
  // Build the appropriate message
  const getAlertMessage = () => {
    if (profileIncomplete && redemptionIncomplete) {
      return {
        prefix: 'Please update your ',
        linkText: 'Profile',
        linkTo: '/dashboard/profile',
        middle: ' and ',
        linkText2: 'Redemptions',
        linkTo2: '/dashboard/redemptions',
        suffix: ' as soon as possible for payments.',
        hasTwoLinks: true
      };
    } else if (profileIncomplete) {
      return {
        prefix: 'Please update your ',
        linkText: 'Profile',
        linkTo: '/dashboard/profile',
        suffix: ' as soon as possible.',
        hasTwoLinks: false
      };
    } else if (redemptionIncomplete) {
      return {
        prefix: 'Please update your ',
        linkText: 'Redemptions',
        linkTo: '/dashboard/redemptions',
        suffix: ' as soon as possible for payments.',
        hasTwoLinks: false
      };
    }
    return null;
  };
  
  const alertMessage = getAlertMessage();
  
  const middleContent = showAlert && alertMessage ? (
    <Alert 
      severity="warning" 
      sx={{ 
        fontSize: { xs: '0.9rem', sm: '1rem' },
        textAlign: 'center',
        '& .MuiAlert-message': {
          width: '100%',
          textAlign: 'center'
        }
      }}
    >
      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, textAlign: 'center', color: '#dc2626' }}>
        ⚠️ Important Notice
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'center' }}>
        {alertMessage.prefix}
        <Link 
          component="button"
          variant="body2"
          onClick={() => navigate(alertMessage.linkTo)}
          sx={{ 
            fontWeight: 600,
            textDecoration: 'underline',
            color: 'warning.dark',
            cursor: 'pointer'
          }}
        >
          {alertMessage.linkText}
        </Link>
        {alertMessage.hasTwoLinks && (
          <>
            {alertMessage.middle}
            <Link 
              component="button"
              variant="body2"
              onClick={() => navigate(alertMessage.linkTo2)}
              sx={{ 
                fontWeight: 600,
                textDecoration: 'underline',
                color: 'warning.dark',
                cursor: 'pointer'
              }}
            >
              {alertMessage.linkText2}
            </Link>
          </>
        )}
        {alertMessage.suffix}
      </Typography>
    </Alert>
  ) : null;

  const summaryWithBalance =
    availableBalance === null
      ? (summary ?? {})
      : { ...(summary ?? {}), availableBalance };

  return (
    <Box
      sx={(t) => ({
        // Stretch the page background all the way to the bottom of the
        // dashboard shell so the bottom doesn't fall back to the lighter
        // panel grey behind a short page.
        minHeight: {
          xs: 'calc(100vh - 56px - 24px)',
          sm: 'calc(100vh - 64px - 40px)',
        },
        background:
          t.palette.mode === 'dark'
            ? `radial-gradient(ellipse 120% 80% at 50% -20%, ${alpha(portal.cyan, 0.12)} 0%, transparent 50%),
               radial-gradient(ellipse 80% 50% at 100% 50%, ${alpha(portal.purple, 0.08)} 0%, transparent 45%),
               ${t.palette.background.default}`
            : `linear-gradient(180deg, ${alpha(portal.cyan, 0.06)} 0%, ${t.palette.background.default} 35%)`,
      })}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 2 } }}>
        <DashboardPageHeader
          icon={TrendingUp}
          title="My Summary"
          subtitle="Track your engagement insights, earnings progress, and account readiness in one place."
          sx={summaryHeaderSurfaceSx}
        />
      </Container>
      <WelcomeHeader
        user={summaryWithBalance}
        middleContent={middleContent}
        hideWelcomePromo
        streakDiamondMetrics={streakDiamondMetrics}
        earningsSummaryLoaded={!loading}
        cacheOnly
      />
      
      <Container 
        maxWidth="lg" 
        sx={{ 
          px: { xs: 1, sm: 2 },
          pb: { xs: 2, sm: 4 },
          pt: { xs: 2, sm: 3 }
        }}
      >
        {/* KYC Status Indicator - Hidden as not integrated yet */}

        {/* Automatic Monthly Payments section removed - using new withdrawal system */}
      </Container>
    </Box>
  );
}

export default EarningsSummary;
